import React from 'react';
import { Modal } from 'react-bootstrap';
import styled from 'styled-components';
import { useTheme } from '../../middleware/ThemeContext';
import { Theme } from '../../utils/themes';
import StyledText from '../StyledComponents/StyledText';
import Avatar from '../Avatar/Avatar';
import { ContactDisplayField, ContactInfo } from './PublicDirectory';
import { hexToRgba } from '../../utils/helper';
import { StyledIconBg } from '../StyledComponents/StyledIconBg';
import CloseIcon from '../../assets/icons/CloseIcon';

interface ContactDetailsModalProps {
    contact: ContactInfo | null;
    isOpen: boolean;
    onClose: () => void;
    displayName?: string;
    displayEmail?: string;
    displayImageUrl?: string;
    fields?: ContactDisplayField[];
}

const StyledModal = styled(Modal) <{ theme: Theme }>`
    .modal-dialog {
        max-width: 745px;
        align-self: center;
        border-radius:5px;
    }
`;

const StyledModalHeader = styled(Modal.Header) <{ theme: Theme, closeVariant: string }>`
    border: none;
    background: ${({ theme, closeVariant }) => closeVariant === 'dark' ? theme?.CreateFormTheme?.colors?.background : hexToRgba(theme?.CorporateTheme?.colors?.blueberry, 0.15)};
    height:88px;
    border-radius:8px;
    position: relative;
    justify-content: end;
    align-items: flex-start;
`;

const HeaderContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 30px 40px;
    width: 100%;
    text-align: center;
`;

const AvatarWrapper = styled.div`
position:absolute;
top:40px;
left:50%;
transform:translateX(-50%);
z-index:1;
`;

const ContactName = styled.h2`
    margin: 12px 0 4px 0;
    font-size: 22px;
    font-weight: 600;
    color: #1a202c;
`;

const ContactEmail = styled.p`
    margin: 0;
    font-size: 14px;
    color: #718096;
`;

const StyledModalBody = styled(Modal.Body) <{ theme: Theme }>`
    background-color: ${({ theme }) => theme?.CorporateTheme?.colors?.background || '#ffffff'};
    border-radius:5px;
`;
const ScrollableContainer = styled.div <{ theme: Theme }>`
    overflow-x: auto;
    max-height: 380px;

`;

const TableWrapper = styled.div <{ theme: Theme }>`
    border: 1px solid ${({ theme }) => theme?.CorporateTheme?.colors?.border || '#e2e8f0'};
    border-radius:5px;
    margin-right:5px;

`;

const FieldsTable = styled.table<{ theme: Theme }>`
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;

    th, td {
        padding: 12px 16px;
        border-bottom: 1px solid #e2e8f0;
        vertical-align: top;
        text-align: left;
    }

    tbody tr:nth-child(even) td {
        background-color: ${({ theme }) => theme?.CorporateTheme?.colors?.panelBG || '#f7f9fc'};
    }

    tr:last-child td {
        border-bottom: none;
    }
`;

const FieldNameCell = styled.td<{ theme: Theme }>`
    font-weight: 600;
    color: ${({ theme }) => theme?.CorporateTheme?.colors?.textSecondary};
    font-size: 13px;
    width: 35%;
`;

const TableHeadCell = styled.th<{ theme: Theme }>`
    font-weight: 600;
    color: ${({ theme }) => theme?.CorporateTheme?.colors?.textSecondary};
    font-size: 14px;
    background-color: ${({ theme }) => theme?.CorporateTheme?.colors?.panelBG};
`;

const FieldValueCell = styled.td<{ theme: Theme }>`
    color: ${({ theme }) => theme?.CorporateTheme?.colors?.headerColor || '#1a202c'};
    font-size: 14px;
    word-break: break-word;
`;

const ContactDetailsModal: React.FC<ContactDetailsModalProps> = ({
    contact,
    isOpen,
    onClose,
    displayName,
    displayEmail,
    displayImageUrl,
    fields = [],
}) => {
    const { theme } = useTheme();
    const savedTheme = localStorage.getItem("appTheme") || "";

    if (!contact) return null;

    const fieldsToDisplay = fields;

    return (
        <StyledModal show={isOpen} onHide={onClose} size='lg' centered>
            <div className='p-2' style={{ background: theme?.CorporateTheme?.colors?.background }}>
                <StyledModalHeader closeVariant={savedTheme} theme={theme}>
                    <StyledIconBg theme={theme} onClick={onClose}>
                        <CloseIcon color={theme.CorporateTheme.colors.headerColor} />
                    </StyledIconBg>
                    <AvatarWrapper>
                        <Avatar
                            imageUrl={displayImageUrl || contact?.contactSignedUrl || contact?.signedUrl}
                            name={displayName || contact?.contactName || contact?.fullName || contact?.name}
                            size={93}
                            border={`2px solid ${theme?.CorporateTheme?.colors?.background || '#ffffff'}`}
                        />
                    </AvatarWrapper>

                </StyledModalHeader>

                <StyledModalBody theme={theme}>
                    <div className='text-center mt-5 mb-4'>
                        <StyledText fontSize='20px' fontWeight={700} textColor={theme.CorporateTheme.colors.primary}>{displayName || contact.contactName || contact.fullName || contact.name}</StyledText>
                        <StyledText fontSize='16px' fontWeight={400} textColor={theme.CorporateTheme.colors.textSecondary}>{displayEmail || contact.contactEmail || contact.email}</StyledText>
                    </div>
                    <ScrollableContainer>
                        <TableWrapper theme={theme}>
                            <FieldsTable theme={theme}>
                                <thead>
                                    <tr>
                                        <TableHeadCell theme={theme} colSpan={1} style={{ borderRight: `1px solid ${theme.CorporateTheme.colors.border}` }}>
                                            <StyledText fontSize='13px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Field Name</StyledText>
                                        </TableHeadCell>
                                        <TableHeadCell theme={theme} colSpan={2}>
                                            <StyledText fontSize='13px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Values</StyledText>
                                        </TableHeadCell>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fieldsToDisplay.length > 0 ? fieldsToDisplay.map((field, index) => (
                                        <tr key={index}>
                                            <FieldNameCell theme={theme} colSpan={1} style={{ borderRight: `1px solid ${theme.CorporateTheme.colors.border}` }}>
                                                <StyledText fontSize='13px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}>{field.label}</StyledText>
                                            </FieldNameCell>
                                            <FieldValueCell theme={theme} colSpan={2}>
                                                <StyledText fontSize='13px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}>{field.value}</StyledText>
                                            </FieldValueCell>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <FieldValueCell theme={theme} colSpan={3}>
                                                <StyledText fontSize='13px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}>
                                                    No additional fields available
                                                </StyledText>
                                            </FieldValueCell>
                                        </tr>
                                    )}
                                </tbody>
                            </FieldsTable>
                        </TableWrapper>
                    </ScrollableContainer>
                </StyledModalBody>
            </div>
        </StyledModal>
    );
};

export default ContactDetailsModal;
